/**
 * RecipeEditMode — the one recipe editor, for a new recipe and an existing one.
 *
 * « L'écrire » used to open a different form (`EditRecipeModal`) from
 * « Modifier », and the two disagreed: this one had no unit box, saved
 * `unit: ''` and kept the stale text of a changed row (#792). Ingredients are
 * now one line each, read by the same parser as every imported recipe; see
 * `ingredientEditLines.ts`.
 */
import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Platform,
  ActivityIndicator,
  KeyboardAvoidingView,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';
import { Recipe } from '../../types/recipe';
import { ingredientLinesOf, ingredientsFromLines } from '../../utils/ingredientEditLines';
import { correctionEvent, type LineLanguage } from '../../utils/ingredientLineCorrection';
import { editedTimes } from '../../utils/editedTimes';
import { trackEvent } from '../../services/analyticsService';
import { IngredientLineHint } from './IngredientLineHint';
import { RemoteImage } from '../shared/RemoteImage';
import { aboveFontFloor } from '../../utils/fontFloor';

const isWeb = Platform.OS === 'web';

interface DraftLine {
  /** Stable across removals, so a deleted row never hands its focus to the next. */
  key: number;
  text: string;
}

interface DraftStep {
  step: string;
}

interface RecipeEditModeProps {
  recipe: Recipe;
  /** A recipe being written from scratch (« L'écrire »): titled « Nouvelle recette ». */
  isNew?: boolean;
  onCancel: () => void;
  /** Resolves true once the change is stored; the editor stays open otherwise. */
  onSave: (updates: Partial<Recipe>) => Promise<boolean>;
}

// ---------- Small field primitives ----------

const Field = ({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType,
  serif,
  testID,
  autoCapitalize,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  keyboardType?: 'default' | 'numeric' | 'url';
  serif?: boolean;
  testID?: string;
  autoCapitalize?: 'none' | 'sentences';
}) => {
  const [focused, setFocused] = useState(false);
  return (
    <View style={styles.fieldWrap}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={[styles.fieldInput, serif && styles.fieldInputSerif, focused && styles.fieldInputFocused]}
        {...(serif ? aboveFontFloor : {})}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Paprika.textMuted}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        testID={testID}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </View>
  );
};

const Area = ({
  label,
  value,
  onChangeText,
  placeholder,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
}) => {
  const [focused, setFocused] = useState(false);
  return (
    <View style={styles.fieldWrap}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={[styles.areaInput, focused && styles.fieldInputFocused]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Paprika.textMuted}
        multiline
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </View>
  );
};

// ---------- Main ----------

export const RecipeEditMode: React.FC<RecipeEditModeProps> = ({ recipe, isNew = false, onCancel, onSave }) => {
  const { t, i18n } = useTranslation();
  // The line's language decides « de sauce » vs "sauce", and which units the picker offers.
  const lineLang: LineLanguage =
    recipe.originalLanguage === 'en' || recipe.originalLanguage === 'fr'
      ? recipe.originalLanguage
      : i18n.language?.startsWith('en') ? 'en' : 'fr';

  // A new recipe is created as « Nouvelle recette » so the editor has a document
  // to save into; the cook names it, so the field starts empty.
  const [title, setTitle] = useState(() =>
    isNew && recipe.title === t('addRecipe.newRecipeTitle') ? '' : recipe.title || '',
  );
  const [summary, setSummary] = useState(recipe.summary || '');
  const [image, setImage] = useState(recipe.image || '');
  // « Préparation » and « Prêt en », as the recipe screen shows them. The first
  // stays blank until a timeline or a person has put a number there.
  const [openedTimes] = useState(() => ({
    handsOnText: String(recipe.handsOnMinutes || ''),
    readyText: String(recipe.readyInMinutes || ''),
  }));
  const [handsOnTime, setHandsOnTime] = useState(openedTimes.handsOnText);
  const [readyTime, setReadyTime] = useState(openedTimes.readyText);
  const [timeError, setTimeError] = useState<string | null>(null);
  const [servings, setServings] = useState(String(recipe.servings || (isNew ? 4 : '')));
  const [sourceName, setSourceName] = useState(recipe.sourceName || '');
  const [sourceUrl, setSourceUrl] = useState(recipe.sourceUrl || '');
  const [isSaving, setIsSaving] = useState(false);

  const nextKey = useRef(0);
  const newLine = (text = ''): DraftLine => ({ key: nextKey.current++, text });
  // An empty recipe opens on one blank line and one blank step, ready to type into.
  const [openedLines] = useState(() => ingredientLinesOf(recipe));
  const [lines, setLines] = useState<DraftLine[]>(() =>
    openedLines.length ? openedLines.map(text => newLine(text)) : [newLine()],
  );
  // Only the line last typed in shows how it is read; every line at once is
  // noise. It follows focus but outlives the blur: tapping one of its pills
  // blurs the field (and drops the phone's keyboard) before the tap lands, so a
  // hint tied to focus vanished from under the finger.
  const [focusedLine, setFocusedLine] = useState<number | null>(null);
  const [hintLine, setHintLine] = useState<number | null>(null);
  const [pickerLine, setPickerLine] = useState<number | null>(null);
  const [correctedLine, setCorrectedLine] = useState<number | null>(null);
  // What the line said when its picker opened, so a correction is logged once,
  // as a whole, when the picker closes — not once per step of the stepper.
  const pickerSession = useRef<{ key: number; before: string; otherUnit?: string } | null>(null);
  const linesRef = useRef(lines);
  linesRef.current = lines;

  const closePicker = () => {
    const session = pickerSession.current;
    pickerSession.current = null;
    setPickerLine(null);
    if (!session) return;
    const after = linesRef.current.find(line => line.key === session.key)?.text ?? '';
    const event = correctionEvent(session.before, after, lineLang, session.otherUnit);
    if (event) trackEvent('ingredient_line_corrected', event);
  };
  const togglePicker = (key: number) => {
    if (pickerLine === key) {
      closePicker();
      return;
    }
    closePicker();
    pickerSession.current = { key, before: linesRef.current.find(line => line.key === key)?.text ?? '' };
    setPickerLine(key);
  };
  const rewriteLine = (key: number, text: string, otherUnit?: string) => {
    setLines(prev => prev.map(line => (line.key === key ? { ...line, text } : line)));
    linesRef.current = linesRef.current.map(line => (line.key === key ? { ...line, text } : line));
    if (otherUnit && pickerSession.current?.key === key) pickerSession.current.otherUnit = otherUnit;
    setCorrectedLine(key);
  };

  const [steps, setSteps] = useState<DraftStep[]>(() => {
    const existing = (recipe.instructions || []).map(s => ({ step: s.step || '' }));
    return existing.length ? existing : [{ step: '' }];
  });

  const pickImage = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.8,
      allowsEditing: true,
      aspect: [4, 3],
    });
    if (!result.canceled && result.assets[0]) {
      setImage(result.assets[0].uri);
    }
  };

  const updateLine = (key: number, text: string) => {
    setLines(prev => prev.map(line => (line.key === key ? { ...line, text } : line)));
    if (correctedLine === key) setCorrectedLine(null);
  };
  const addLine = () => setLines(prev => [...prev, newLine()]);
  const removeLine = (key: number) => {
    // A deleted line is not a correction: drop its picker without logging.
    if (pickerSession.current?.key === key) {
      pickerSession.current = null;
      setPickerLine(null);
    }
    if (hintLine === key) setHintLine(null);
    setLines(prev => prev.filter(line => line.key !== key));
  };

  const updateStep = (idx: number, step: string) => {
    setSteps(prev => prev.map((s, i) => (i === idx ? { step } : s)));
  };
  const addStep = () => setSteps(prev => [...prev, { step: '' }]);
  const removeStep = (idx: number) => setSteps(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!title.trim()) return;
    // Sent only when the cook changed it: a title-only save must not make the
    // model's time the cook's.
    const times = editedTimes(handsOnTime, readyTime, openedTimes);
    if ('error' in times) {
      setTimeError(
        times.error === 'not_a_number'
          ? t('editRecipe.timeNotANumber')
          : t('editRecipe.timeHandsOnExceedsReady'),
      );
      return;
    }
    setTimeError(null);
    closePicker();
    setIsSaving(true);
    try {
      const updates: Partial<Recipe> = {
        title: title.trim(),
        summary: summary.trim() || undefined,
        image: image.trim() || undefined,
        ...times,
        servings: parseInt(servings, 10) || recipe.servings,
      };
      // Written only when changed, so clearing the field clears it.
      if (sourceName.trim() !== (recipe.sourceName ?? '').trim()) updates.sourceName = sourceName.trim();
      if (sourceUrl.trim() !== (recipe.sourceUrl ?? '').trim()) updates.sourceUrl = sourceUrl.trim();

      // Sent only when the lines changed: the copy on screen can be a first
      // paint built from the list's index, and rewriting from it would replace
      // the stored ingredients with a thinner version of themselves.
      const typed = lines.map(line => line.text.trim()).filter(Boolean);
      const ingredients = ingredientsFromLines(typed, recipe.extendedIngredients ?? []);
      if (ingredients.length > 0 && typed.join('\n') !== openedLines.join('\n')) {
        updates.extendedIngredients = ingredients;
      }

      const cleanSteps = steps.filter(s => s.step.trim());
      if (cleanSteps.length > 0) {
        updates.instructions = cleanSteps.map((s, idx) => ({ number: idx + 1, step: s.step.trim() }));
      }

      await onSave(updates);
    } catch (e) {
      console.error('Inline edit save failed:', e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View style={styles.overlay}>
      {/* Sticky dark bar */}
      <View style={styles.stickyBar}>
        <View style={styles.stickyBarLeft}>
          <Feather name="edit-2" size={16} color="#fff" />
          <View style={styles.stickyBarTextBlock}>
            <Text style={styles.stickyBarTitle}>
              {isNew
                ? t('recipeDetail.newRecipeTitle', 'Nouvelle recette')
                : t('recipeDetail.editRecipe', 'Modifier la recette')}
            </Text>
            {!isWeb ? null : (
              <Text style={styles.stickyBarSub}>
                {isNew
                  ? t('recipeDetail.newRecipeHint', 'Rien n’est gardé avant d’enregistrer.')
                  : t('recipeDetail.editHint', 'Vos changements ne sont enregistrés qu’en validant.')}
              </Text>
            )}
          </View>
        </View>
        <View style={styles.stickyBarActions}>
          <TouchableOpacity
            style={styles.cancelBtn}
            onPress={onCancel}
            activeOpacity={0.75}
            disabled={isSaving}
            testID="edit-recipe-cancel"
          >
            <Text style={styles.cancelBtnText}>{t('common.cancel', 'Annuler')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.saveBtn, (!title.trim() || isSaving) && styles.saveBtnDisabled]}
            onPress={handleSave}
            activeOpacity={0.85}
            disabled={!title.trim() || isSaving}
            testID="edit-recipe-save"
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Feather name="check" size={16} color="#fff" />
            )}
            <Text style={styles.saveBtnText}>{t('common.save', 'Enregistrer')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          style={styles.flex}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Hero with change-photo overlay */}
          <TouchableOpacity style={styles.hero} onPress={pickImage} activeOpacity={0.85}>
            <RemoteImage uri={image} style={styles.heroImage} resizeMode="cover" fallback={null} />
            {image ? <View style={styles.heroScrim} /> : null}
            <View style={styles.changePhotoButton}>
              <Feather name={image ? 'camera' : 'plus'} size={16} color="#fff" />
              <Text style={styles.changePhotoText}>
                {image
                  ? t('recipeDetail.changePhoto', 'Changer la photo')
                  : t('recipeDetail.addPhoto', 'Ajouter une photo')}
              </Text>
            </View>
          </TouchableOpacity>

          <View style={styles.body}>
            <Field
              label={t('recipeDetail.titleLabel', 'TITRE')}
              value={title}
              onChangeText={setTitle}
              placeholder={t('recipeDetail.titlePlaceholder', 'Nom de la recette')}
              serif
              testID="edit-recipe-title"
            />

            <View style={styles.row}>
              <View style={styles.rowItem}>
                <Field
                  label={t('editRecipe.handsOnLabel')}
                  value={handsOnTime}
                  onChangeText={v => { setHandsOnTime(v); setTimeError(null); }}
                  keyboardType="numeric"
                  placeholder="0"
                  testID="edit-recipe-hands-on"
                />
              </View>
              <View style={styles.rowItem}>
                <Field
                  label={t('editRecipe.readyInLabel')}
                  value={readyTime}
                  onChangeText={v => { setReadyTime(v); setTimeError(null); }}
                  keyboardType="numeric"
                  placeholder="0"
                  testID="edit-recipe-ready-in"
                />
              </View>
              <View style={styles.rowItem}>
                <Field
                  label={t('recipeDetail.servingsLabel', 'PORTIONS')}
                  value={servings}
                  onChangeText={setServings}
                  keyboardType="numeric"
                  placeholder="0"
                />
              </View>
            </View>

            {timeError != null && (
              <Text style={styles.timeError} testID="edit-recipe-time-error">{timeError}</Text>
            )}

            <Area
              label={t('recipeDetail.descriptionLabel', 'DESCRIPTION')}
              value={summary}
              onChangeText={setSummary}
              placeholder={t('recipeDetail.descriptionPlaceholder', 'Une courte description…')}
            />

            {/* Ingredients: one line each, written the way the recipe prints it */}
            <Text style={styles.groupTitle}>{t('recipeDetail.ingredients', 'Ingrédients')}</Text>
            {lines.map((line, idx) => {
              const showHint = hintLine === line.key;
              return (
                <View key={line.key} style={styles.ingBlock}>
                  <View style={styles.ingRow}>
                    <TextInput
                      style={[styles.fieldInput, styles.ingLine, focusedLine === line.key && styles.fieldInputFocused]}
                      value={line.text}
                      onChangeText={v => updateLine(line.key, v)}
                      placeholder={t('recipeDetail.ingredientLinePlaceholder', 'ex. 3 L de sauce tomate')}
                      placeholderTextColor={Paprika.textMuted}
                      onFocus={() => {
                        if (pickerLine !== null && pickerLine !== line.key) closePicker();
                        setFocusedLine(line.key);
                        setHintLine(line.key);
                      }}
                      onBlur={() => setFocusedLine(current => (current === line.key ? null : current))}
                      testID={`edit-ingredient-line-${idx}`}
                    />
                    <TouchableOpacity
                      style={styles.deleteRowButton}
                      onPress={() => removeLine(line.key)}
                      hitSlop={6}
                      accessibilityLabel={t('recipeDetail.removeIngredient', 'Retirer cet ingrédient')}
                    >
                      <Feather name="trash-2" size={16} color={Paprika.destructive} />
                    </TouchableOpacity>
                  </View>
                  {showHint ? (
                    <IngredientLineHint
                      text={line.text}
                      lang={lineLang}
                      pickerOpen={pickerLine === line.key}
                      corrected={correctedLine === line.key}
                      onTogglePicker={() => togglePicker(line.key)}
                      onRewrite={(text, otherUnit) => rewriteLine(line.key, text, otherUnit)}
                      onClosePicker={closePicker}
                    />
                  ) : null}
                </View>
              );
            })}
            <TouchableOpacity style={styles.ghostButton} onPress={addLine} activeOpacity={0.7} testID="edit-ingredient-add">
              <Feather name="plus" size={15} color={Paprika.accent} />
              <Text style={styles.ghostButtonText}>{t('recipeDetail.addIngredient', 'Ajouter un ingrédient')}</Text>
            </TouchableOpacity>

            {/* Steps */}
            <Text style={styles.groupTitle}>{t('recipeDetail.steps', 'Préparation')}</Text>
            {steps.map((s, idx) => (
              <View key={idx} style={styles.stepRow}>
                <Text style={styles.stepNumber}>{String(idx + 1).padStart(2, '0')}</Text>
                <TextInput
                  style={[styles.areaInput, styles.stepInput]}
                  value={s.step}
                  onChangeText={v => updateStep(idx, v)}
                  placeholder={t('recipeDetail.stepPlaceholder', 'Décrivez cette étape…')}
                  placeholderTextColor={Paprika.textMuted}
                  multiline
                />
                <TouchableOpacity style={styles.deleteRowButton} onPress={() => removeStep(idx)} hitSlop={6}>
                  <Feather name="trash-2" size={16} color={Paprika.destructive} />
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity style={styles.ghostButton} onPress={addStep} activeOpacity={0.7}>
              <Feather name="plus" size={15} color={Paprika.accent} />
              <Text style={styles.ghostButtonText}>{t('recipeDetail.addStep', 'Ajouter une étape')}</Text>
            </TouchableOpacity>

            {/* Source, optional */}
            <View style={styles.sourceBlock}>
              <Text style={styles.fieldLabel}>{t('recipeDetail.sourceLabel', 'SOURCE (FACULTATIF)')}</Text>
              <Field
                label={t('recipeDetail.sourceAuthorLabel', 'AUTEUR')}
                value={sourceName}
                onChangeText={setSourceName}
                placeholder={t('recipeDetail.sourceAuthorPlaceholder', 'Qui a écrit la recette')}
                testID="edit-recipe-author"
              />
              <Field
                label={t('recipeDetail.sourceLinkLabel', 'LIEN')}
                value={sourceUrl}
                onChangeText={setSourceUrl}
                placeholder="https://…"
                keyboardType="url"
                autoCapitalize="none"
                testID="edit-recipe-source-url"
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: Paprika.background,
    zIndex: 30,
  },
  flex: { flex: 1 },
  stickyBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: PaprikaSpacing.sm,
    backgroundColor: '#1b1c1a',
    paddingHorizontal: PaprikaSpacing.md,
    paddingTop: isWeb ? PaprikaSpacing.md : 52,
    paddingBottom: PaprikaSpacing.md,
  },
  stickyBarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    flexShrink: 1,
  },
  stickyBarTextBlock: {
    flexShrink: 1,
  },
  stickyBarTitle: {
    ...PaprikaTypography.labelLarge,
    color: '#fff',
  },
  stickyBarSub: {
    ...PaprikaTypography.caption,
    color: 'rgba(255,255,255,0.6)',
  },
  stickyBarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  cancelBtn: {
    paddingVertical: 8,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.full,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
  },
  cancelBtnText: {
    ...PaprikaTypography.labelMedium,
    color: '#fff',
  },
  saveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.xs,
    paddingVertical: 8,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.accent,
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
  saveBtnText: {
    ...PaprikaTypography.labelMedium,
    color: '#fff',
  },
  scrollContent: {
    paddingBottom: PaprikaSpacing.xxl * 2,
  },
  hero: {
    height: isWeb ? 180 : 128,
    backgroundColor: Paprika.surfaceLow,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  heroImage: {
    ...StyleSheet.absoluteFillObject,
  },
  heroScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.34)',
  },
  changePhotoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.xs,
    backgroundColor: 'rgba(27,28,26,0.72)',
    borderRadius: PaprikaRadius.full,
    paddingVertical: 8,
    paddingHorizontal: PaprikaSpacing.md,
  },
  changePhotoText: {
    ...PaprikaTypography.labelMedium,
    color: '#fff',
  },
  body: {
    padding: PaprikaSpacing.lg,
    gap: PaprikaSpacing.md,
    maxWidth: 760,
    width: '100%',
    alignSelf: 'center',
  },
  fieldWrap: {
    gap: PaprikaSpacing.xs,
  },
  fieldLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    letterSpacing: 0.6,
    // Every label is uppercase; the strings of the two time fields are not.
    textTransform: 'uppercase',
  },
  fieldInput: {
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.cardBg,
    paddingVertical: 10,
    paddingHorizontal: PaprikaSpacing.md,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },
  fieldInputSerif: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
  },
  fieldInputFocused: {
    borderColor: Paprika.accent,
  },
  areaInput: {
    minHeight: 72,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.cardBg,
    padding: PaprikaSpacing.md,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    textAlignVertical: 'top',
  },
  row: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
  },
  rowItem: {
    flex: 1,
  },
  timeError: {
    ...PaprikaTypography.caption,
    color: Paprika.destructive,
  },
  groupTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginTop: PaprikaSpacing.md,
  },
  ingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  ingBlock: {
    gap: PaprikaSpacing.xs,
  },
  ingLine: {
    flex: 1,
  },
  sourceBlock: {
    marginTop: PaprikaSpacing.md,
    padding: PaprikaSpacing.md,
    gap: PaprikaSpacing.sm,
    borderRadius: PaprikaRadius.lg,
    backgroundColor: Paprika.surfaceLow,
  },
  deleteRowButton: {
    width: 38,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: PaprikaRadius.sm,
  },
  ghostButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.xs,
    paddingVertical: PaprikaSpacing.sm,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
  },
  ghostButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PaprikaSpacing.sm,
  },
  stepNumber: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 28,
    color: '#e6d8c8',
    width: 36,
    marginTop: 6,
  },
  stepInput: {
    flex: 1,
  },
});

export default RecipeEditMode;
